import Link from 'next/link';
import { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';

import { apiPostV1CompetitionByIdPresentationByIdVote } from '~/api/v1/competitions/[id]/presentations/[id]/vote/post';
import { ApiPresentation } from '~/api/v1/presentations/all_index/get';
import { ApiBodyReview, apiPostV1Reviews } from '~/api/v1/reviews/post';
import { useFetch } from '~/hooks/useFetch';
import { CurrentUserState } from '~/state/currentUser';
import { createObjectURL, media } from '~/utils';
import { getLoginUrlWithRedirectTo } from '~/utils/getLoginUrlWithRedirectTo';
import { toBase64 } from '~/utils/toBase64';

import CloseIcon from '@public/images/close.svg';
import ImageIcon from '@public/images/image-icon.svg';

type Props = {
  isVote: boolean;
  competitionId?: string | number;
  corpFurusatoId?: number;
  orgId?: number;
  presentation?: ApiPresentation;
  initialImage?: File;
  onClose: () => void;
  onSuccess: () => void;
};

export function ModalComment({
  isVote,
  competitionId,
  corpFurusatoId,
  orgId,
  presentation,
  initialImage,
  onClose,
  onSuccess,
}: Props) {
  const currentUser = useRecoilValue(CurrentUserState);
  const [text, setText] = useState('');
  const [image, setImage] = useState(initialImage);

  const ref = useRef<HTMLInputElement>(null);
  const { fetch, isLoading } = useFetch();

  const getImage = initialImage || image;

  if (!presentation) return null;
  if (isVote && !competitionId) return null;
  if (!isVote && !currentUser) {
    return (
      <Wrapper>
        <StyledOverlay isVote={isVote} onClick={onClose} />
        <Main isVote={isVote} onClick={(e) => e.stopPropagation()}>
          <StyledCloseIconBox>
            <CloseIcon onClick={onClose} />
          </StyledCloseIconBox>
          <SignInText>コメントするには ログイン / 会員登録 をお願いします。</SignInText>
          <SignInButton href={getLoginUrlWithRedirectTo()} isVote={false}>
            ログイン/会員登録
          </SignInButton>
        </Main>
      </Wrapper>
    );
  }

  const onPostComment = async () => {
    if (text.length < 5) {
      toast.error('コメントは5文字以上入力して下さい。');
      return;
    }

    const image = getImage ? await toBase64(getImage) : '';
    const postableType = currentUser?.type === 'user' ? 'User' : 'Organization';
    const postableId = currentUser !== null ? currentUser?.id : presentation.organization.id;

    const body = {
      postable_type: postableType,
      postable_id: postableId,
      text,
      corp_furusato_id: corpFurusatoId,
      organization_id: orgId || (presentation && presentation.organization.id),
      presentation_id: presentation && presentation.id,
      images: [image],
    };

    await fetch(() => apiPostV1Reviews(body))
      .then(() => {
        toast.success('コメントを投稿しました。');

        setText('');
        onSuccess();
      })
      .catch((error) => {
        console.error(error);
        toast.error('コメントの投稿に失敗しました');
      });
  };

  const onPostVote = async () => {
    if (!competitionId) {
      toast.error('投票はできません');
      return;
    }

    await fetch(() =>
      apiPostV1CompetitionByIdPresentationByIdVote({
        competitionId,
        presentationId: presentation.id,
      }),
    )
      .then(() => {
        toast.success('ご投票ありがとうございます！');

        onSuccess();
      })
      .catch((error) => {
        console.error(error);
        toast.error('投票に失敗しました');
      });
  };

  const onPostCommentAndVote = async () => {
    if (!competitionId) {
      toast.error('投票はできません');
      return;
    }
    if (text.length < 5) {
      toast.error('コメントは5文字以上入力して下さい。');
      return;
    }

    const image = getImage ? await toBase64(getImage) : '';
    const postableType = currentUser !== null ? 'User' : 'Organization';
    const postableId = currentUser !== null ? currentUser?.id : presentation.organization.id;

    const body = {
      postable_type: postableType,
      postable_id: postableId,
      text,
      corp_furusato_id: corpFurusatoId,
      organization_id: orgId || (presentation && presentation.organization.id),
      presentation_id: presentation && presentation.id,
      images: [image],
    } satisfies ApiBodyReview;

    try {
      await fetch(() =>
        Promise.all([
          apiPostV1Reviews(body),
          apiPostV1CompetitionByIdPresentationByIdVote({
            competitionId,
            presentationId: presentation.id,
          }),
        ]),
      );

      toast.success('コメントおよび投票に成功しました');
      setText('');

      onSuccess();
    } catch (error) {
      console.error(error);
      toast.success('コメントおよび投票に失敗しました');
    }
  };

  return (
    <div>
      <ImageInput
        ref={ref}
        onChange={(e) => {
          setImage(e.target?.files?.[0]);
        }}
        type='file'
        accept='image/*'
      />
      <Wrapper>
        <StyledOverlay isVote={isVote} onClick={onClose} />
        <Main isVote={isVote} onClick={(e) => e.stopPropagation()}>
          <StyledCloseIconBox onClick={onClose}>
            <CloseIcon onClick={onClose} />
          </StyledCloseIconBox>
          <TextAreaWrapper>
            <TextArea isVote={isVote} onChange={(e) => setText(e.target.value)} value={text} />
            {getImage && (
              <PreviewWrapper>
                <Preview src={createObjectURL(getImage)} />
              </PreviewWrapper>
            )}
            <StyledImageIconBox
              onClick={(e) => {
                e.stopPropagation();

                if (ref.current && currentUser) ref.current.click();
              }}
            >
              <ImageIcon />
            </StyledImageIconBox>
          </TextAreaWrapper>
          <Buttons>
            {isVote && <VoteButton onClick={onPostVote}>投票する</VoteButton>}
            {isLoading ? (
              <StyledCommentButton disabled>送信中</StyledCommentButton>
            ) : (
              <CommentButton
                isVote={isVote}
                onClick={isVote ? onPostCommentAndVote : onPostComment}
              >
                {isVote ? 'コメントして投票する' : 'コメントする'}
              </CommentButton>
            )}
          </Buttons>
        </Main>
      </Wrapper>
    </div>
  );
}

const Wrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100dvh;
  z-index: 500;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledOverlay = styled.div<{ isVote: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100dvh;

  background: ${(props) => (props.isVote ? 'rgba(38, 38, 38, 0.7)' : 'rgba(255, 255, 255, 0.7)')};
  backdrop-filter: blur(10px);
`;

const Main = styled.div<{ isVote: boolean }>`
  position: relative;
  width: 800px;
  height: 700px;
  background: ${(props) => (props.isVote ? '#262626' : '#EBEBEB')};
  z-index: 500;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: 10px;
  justify-content: center;

  ${media.mid`
    width: 100%;
    height: 100%;
    border-radius: 0;
  `}
`;

const TextAreaWrapper = styled.div`
  position: relative;
  width: 758px;
  height: 551px;

  ${media.mid`
    width: calc(100% - 40px);
    height: 440px;
  `}
`;

const TextArea = styled.textarea<{ isVote: boolean }>`
  padding: 20px;
  width: 718px;
  height: 511px;
  border: none;
  font-size: 18px;
  line-height: 24px;
  background: ${(props) => (props.isVote ? '#2E2E2E' : '#DEDEDE')};
  border-radius: 15px;
  color: ${(props) => (props.isVote ? '#fff' : '#262626')};

  ${media.mid`
    width: calc(100% - 40px);
    height: 400px;
  `}
`;

const Buttons = styled.div`
  width: 758px;
  margin-top: 20px;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;

  ${media.mid`
    margin-top: 40px;
    flex-direction: column;
    align-items: center;
    width: 100%;
  `}
`;

const VoteButton = styled.div`
  margin-right: 20px;
  flex: 1;
  height: 46px;
  border: 1px solid #ebebeb;
  border-radius: 15px;
  text-align: center;
  font-size: 22px;
  font-weight: bold;
  line-height: 48px;
  color: #ebebeb;
  cursor: pointer;

  ${media.mid`
    margin-right: 0;
    margin-bottom: 20px;
    width: calc(100% - 40px);
  `}
`;

const CommentButton = styled.button<{ isVote: boolean }>`
  cursor: pointer;
  width: 480px;
  height: 48px;
  text-align: center;
  background-color: ${(props) => (props.isVote ? '#EBEBEB' : '#262626')};
  color: ${(props) => (props.isVote ? '#262626' : '#fff')};
  line-height: 48px;
  font-size: 22px;
  font-weight: bold;
  border-radius: 15px;

  ${media.mid`
    width: calc(100% - 40px);
  `}
`;

const StyledCommentButton = styled.button`
  background-color: #868686;
  border-radius: 15px;
  color: #fff;
  cursor: progress;
  font-size: 22px;
  font-weight: bold;
  height: 48px;
  line-height: 48px;
  width: 480px;

  ${media.mid`
    width: calc(100% - 40px);
  `}
`;

const SignInText = styled.div`
  width: 480px;
  height: 280px;
  font-size: 24px;
  line-height: 40px;
  font-weight: bold;
  color: #262626;

  ${media.mid`
    width: calc(100% - 40px);
  `}
`;

const SignInButton = styled(Link)<{ isVote: boolean }>`
  display: block;
  cursor: pointer;
  width: 480px;
  height: 48px;
  text-align: center;
  background-color: ${(props) => (props.isVote ? '#EBEBEB' : '#262626')};
  color: ${(props) => (props.isVote ? '#262626' : '#fff')};
  line-height: 48px;
  font-size: 22px;
  font-weight: bold;
  border-radius: 15px;

  ${media.mid`
    width: calc(100% - 40px);
  `}
`;

const ImageInput = styled.input`
  display: none;
`;

const PreviewWrapper = styled.div`
  position: relative;
`;

const Preview = styled.img`
  position: absolute;
  bottom: 2px;
  right: 45px;
  width: 300px;
  height: 150px;
  object-fit: contain;
  object-position: right bottom;

  ${media.mid`
    width: calc(100% - 100px);
  `}
`;

const StyledCloseIconBox = styled.div`
  position: absolute;
  top: -15px;
  right: -15px;
  cursor: pointer;

  ${media.mid`
  top: 10px;
  right: 10px;
`}
`;

const StyledImageIconBox = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 20px;
  background: #adadad;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  right: 2px;
  bottom: 2px;
  cursor: pointer;
`;
